<template>
  <div>
    <!-- section pour visualisation un MANY-->
    <span v-if="multiplicity">
      <div
        v-for="val in infoValues"
        :key="val.id"
      >
        <DatasLink
          v-if="referenceType"
          :application="application"
          :column-id="val.columnName"
          :display-value="changeListDisplayMany(val)"
          :loaded-references-by-key="{}"
          :reference-type="referenceType"
          :value="val"
        />
        <p
          v-else-if="typeof val === 'string' && val.indexOf('date') === 0"
          :aria-label="datePatternLang(val)"
        >
          {{ datePatternLang(val) }}
        </p>
        <p
          v-else
          class="column"
          :aria-label="val"
        >{{ val }}</p>
      </div>
    </span>
    <!-- modal de dynamique colonne -->
    <b-modal
      v-model="isCardModalActive"
      class="referenceDetails"
    >
      <div class="card">
        <div class="card-header">
          <div class="title card-header-title">
            <p
              field="name"
              :aria-label="columnId"
              style="font-size: 1.5rem"
            >
              {{ columnId }}
            </p>
          </div>
        </div>
        <div class="card-content">
          <div
            v-for="key in modalArrayObj"
            :key="key.id"
            class="columns modalArrayObj"
          >
            <DatasLink
              v-if="key.value"
              :application="application"
              :column-id="key.columnName"
              :loaded-references-by-key="{}"
              :reference-type="referenceType"
              :value="key.value"
            />
            <DatasDynamicLink
              v-if="!key.value.length && key.value.length !== 0"
              :application="application"
              :column-id="key.value"
              :info="!key.value.length && key.value.length !== 0"
              :info-values="key.value"
              :loaded-references-by-key="{}"
              :reference-type="dynamicColumnReferences(key.value)"
            />
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script setup lang="ts">
import services from "@/composable/services";
import { datePatternLang } from "@/composable/application/datePattern";
import {
  computed,
  onMounted,
  toRaw,
  defineAsyncComponent,
  provide,
  PropType,
} from "vue";
import { dataLoader } from "@/composable/data/dataLoader";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { TableColumn } from "@/types/ui/TableColumn";
import i18n from "@/i18n";
import { BModal } from "buefy";

defineOptions({
  name: "DatasManyLink",
});

/* ---------------- props & emits ---------------- */
const emit = defineEmits<{
  (e: "changedRefValues", payload: any): void;
  (e: "changeListDisplayMany", payload: any): void;
}>();
const props = defineProps({
  application: {
    type: Object as PropType<LocalApplicationResult>,
    required: true,
  },
  referenceType: {
    type: String,
    required: true,
  },
  value: {
    type: String,
  },
  infoValues: {
    type: Object,
    required: true,
  },
  referenceValue: {
    type: Object,
  },
  column: {
    type: Object,
  },
  displaysForRow: {
    type: Object,
  },
  loadedReferencesByKey: {
    type: Object,
    required: true,
  },
  multiplicity: Boolean,
  columnId: String,
});

/* ---------------- components async ---------------- */
const DatasLink = defineAsyncComponent(
  () => import("@/components/datas/DatasLink.vue"),
);
const DatasDynamicLink = defineAsyncComponent(
  () => import("@/components/datas/DatasDynamicLink.vue"),
);

/* ---------------- setup logic ---------------- */
let refValues = { active: false } as any;
let isCardModalActive = false;
let modalArrayObj = {} as any;

const dataService = services.dataService;
const loader = dataLoader(services);
provide("reference:dataLoader", loader);

const { getValueDisplay } = loader;

onMounted(async () => {
  for (let val of props.infoValues as any) {
    changeListDisplayMany(val);
  }
});

function changeListDisplayMany(val: string) {
  const item = (props.displaysForRow as any)?.[props.referenceType];
  if (val !== "" && item) {
    for (let i = 0; i < item.length; i++) {
      const changeObjectPoxy = toRaw(item[i]);
      if (changeObjectPoxy.naturalKey.sql === val) {
        return getValueDisplay(
          changeObjectPoxy,
          props.application.configuration.applicationDescription.defaultLanguage,
        );
      }
    }
  }
}

const applicationName = computed(() => {
  return props.application.name;
});

function dynamicColumnReferences(nameId: string) {
  if (
    props.application.configuration.dataDescription[props.referenceType]
      .componentDescriptions[nameId]?.type === "DynamicComponent"
  ) {
    return props.application.configuration.dataDescription[props.referenceType]
      .componentDescriptions[nameId].referenceCheckerType;
  }
  else {
    return props.referenceType;
  }
}

async function openReferenceDetail() {
  isCardModalActive = false;

  const localRefValues
    = refValues.active
      && (props.loadedReferencesByKey as any)[props.referenceType]
      && (props.loadedReferencesByKey as any)[props.referenceType][props.value as any];

  const data = Object.entries(refValues.refValue)
    .filter((entry: any) => !entry[0].startsWith("__display_"))
    .map((entry: any) => ({
      colonne: entry[0],
      valeur: entry[1],
    }))
    .reduce((acc: any, entry: any) => {
      acc.push(entry);
      return acc;
    }, []);

  const columns: TableColumn[] = [
    { id: "column", field: "colonne", label: i18n.global.t("table.column-column"), type: "column" },
    { id: "value", field: "valeur", label: i18n.global.t("table.column-value"), type: "value" },
  ];

  const result = {
    data,
    columns: columns,
    active: true,
    reference: props.referenceType,
  };

  refValues = { ...localRefValues, ...result };

  emit("changedRefValues", {
    key: props.value,
    refType: props.referenceType,
    localRefValues,
  });

  return localRefValues;
}

function showModal() {
  isCardModalActive = true;

  modalArrayObj = Object.entries(props.infoValues as any)
    .filter(a => a[1])
    .map((a) => {
      const obj: any = {};
      obj[a[0]] = a[1];
      return obj;
    });

  for (let j = 0; j < modalArrayObj.length; j++) {
    if (modalArrayObj[j]) {
      const column = Object.keys(modalArrayObj[j])[0];
      const value = Object.values(modalArrayObj[j])[0];
      modalArrayObj[j] = {
        ...modalArrayObj[j],
        column,
        value,
      };
    }
  }
}

/* ---------------- expose to template ---------------- */

defineExpose({
  datePatternLang,
  dynamicColumnReferences,
  openReferenceDetail,
  showModal,
  refValues,
  dataService,
  isCardModalActive,
  modalArrayObj,
  applicationName,
  changeListDisplayMany,
});
</script>
